import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { apiService } from '../services/apiService';
import { useErrorHandling } from '../hooks/useErrorHandling';

const TestSetup: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [testDuration, setTestDuration] = useState<number>(10); // Default 10 minutes
  const [testType, setTestType] = useState<'CHECKERBOARD' | 'LETTER'>('CHECKERBOARD');
  const [isMockup, setIsMockup] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [department, setDepartment] = useState('');
  const [workShift, setWorkShift] = useState<'MORNING' | 'EVENING' | 'NIGHT'>('MORNING');
  const [lastSleepDuration, setLastSleepDuration] = useState('');
  const [sleepQuality, setSleepQuality] = useState<number | undefined>();
  const [sleepinessLevel, setSleepinessLevel] = useState<number | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const { executeWithErrorHandling } = useErrorHandling();

  const handleStartTest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (user) {
      setIsLoading(true);
      await executeWithErrorHandling(async () => {
        const response = await apiService.createTest({
          employeeId,
          employeeName: employeeName || undefined,
          department,
          workShift,
          testType,
          lastSleepDuration: lastSleepDuration ? Number(lastSleepDuration) : undefined,
          sleepQuality,
          sleepinessLevel,
        });
        const testId = response.data?.id;
        if (!testId) throw new Error('Test could not be created. Please try again.');
        console.log('Created test with ID:', testId);

        navigate(`/admin/test/${testId}`, {
          state: {
            testDuration: testDuration * 60 * 1000,
            userId: user.id,
            testType,
            isMockup
          }
        });
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800 p-4 sm:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Set up a Fatigue Check</h1>
            <p className="text-sm text-navy-200 mt-1">Choose the test options and enter participant details.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
              Back to Dashboard
            </Button>
            <Button variant="outline" onClick={logout}>
              Logout
            </Button>
          </div>
        </div>

        <div className="bg-navy-800 rounded-2xl p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-white mb-6">Assessment details</h2>

            <form onSubmit={handleStartTest} className="space-y-5">
              <div>
                <Label htmlFor="testDuration" className="block text-sm font-medium text-navy-100 mb-2">
                  Test Duration
                </Label>
                <Select value={testDuration.toString()} onValueChange={(value) => setTestDuration(parseInt(value, 10))}>
                  <SelectTrigger id="testDuration" className="w-full">
                    <SelectValue placeholder="Select test duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Minute (Quick Test)</SelectItem>
                    <SelectItem value="3">3 Minutes (Short)</SelectItem>
                    <SelectItem value="5">5 Minutes (Standard)</SelectItem>
                    <SelectItem value="10">10 Minutes (Full Assessment)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-navy-300">
                  Actual test duration. The optional mockup before it always runs for 30 seconds.
                </p>
              </div>

              <div>
                <Label htmlFor="testType" className="block text-sm font-medium text-navy-100 mb-2">
                  Test Type
                </Label>
                <Select value={testType} onValueChange={(value) => setTestType(value as 'CHECKERBOARD' | 'LETTER')}>
                  <SelectTrigger id="testType" className="w-full">
                    <SelectValue placeholder="Select test type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CHECKERBOARD">Checkerboard Pattern (Standard)</SelectItem>
                    <SelectItem value="LETTER">Letter Recall (Memory)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-navy-300">
                  {testType === 'LETTER'
                    ? 'Users memorize 3 letters and respond when one appears among 5 random letters.'
                    : 'Users respond as quickly as possible when the checkerboard pattern appears.'}
                </p>
              </div>

              <label className="flex min-h-11 items-start gap-3 text-sm text-navy-100">
                <input
                  type="checkbox"
                  checked={isMockup}
                  onChange={(e) => setIsMockup(e.target.checked)}
                  className="mt-0.5 text-gold-400 focus:ring-gold-500"
                />
                <span>
                  <span className="font-medium">Mockup Test (30 seconds)</span>
                  <span className="block text-xs text-navy-300">
                    Runs a 30-second practice before the actual test. Mockup results are not saved.
                  </span>
                </span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="employeeId" className="block text-sm font-medium text-navy-100 mb-2">
                    Employee ID
                  </Label>
                  <Input
                    id="employeeId"
                    value={employeeId}
                    onChange={(e) => setEmployeeId(e.target.value)}
                    placeholder="Enter employee ID"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="employeeName" className="block text-sm font-medium text-navy-100 mb-2">
                    Employee Name (Optional)
                  </Label>
                  <Input
                    id="employeeName"
                    value={employeeName}
                    onChange={(e) => setEmployeeName(e.target.value)}
                    placeholder="Enter employee name"
                  />
                </div>

                <div>
                  <Label htmlFor="department" className="block text-sm font-medium text-navy-100 mb-2">
                    Department
                  </Label>
                  <Input
                    id="department"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    placeholder="Enter department"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="workShift" className="block text-sm font-medium text-navy-100 mb-2">
                    Work Shift
                  </Label>
                  <Select value={workShift} onValueChange={(value) => setWorkShift(value as 'MORNING' | 'EVENING' | 'NIGHT')}>
                    <SelectTrigger id="workShift" className="w-full">
                      <SelectValue placeholder="Select shift" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MORNING">Morning</SelectItem>
                      <SelectItem value="EVENING">Evening</SelectItem>
                      <SelectItem value="NIGHT">Night</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

              </div>

            <details className="rounded-xl border border-navy-700 p-4">
              <summary className="cursor-pointer font-medium text-navy-100">Add sleep context (optional)</summary>
              <div className="mt-4 grid gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="lastSleepDuration" className="block text-sm font-medium text-navy-100 mb-2">
                    Sleep duration (hours)
                  </Label>
                  <Input
                    id="lastSleepDuration"
                    type="number"
                    min="0"
                    step="0.1"
                    value={lastSleepDuration}
                    onChange={(e) => setLastSleepDuration(e.target.value)}
                  />
                </div>

                <fieldset>
                  <legend className="mb-2 text-sm font-medium text-navy-100">Sleep quality (1 poor, 4 best)</legend>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 2, 3, 4].map((value) => (
                      <label key={value} className="flex min-h-11 items-center gap-2 text-sm text-navy-200">
                        <input type="radio" name="sleepQuality" value={value} checked={sleepQuality === value} onChange={() => setSleepQuality(value)} className="text-gold-400 focus:ring-gold-500" />
                        {value}
                      </label>
                    ))}
                  </div>
                </fieldset>

                <fieldset className="sm:col-span-2">
                  <legend className="mb-2 text-sm font-medium text-navy-100">Current sleepiness</legend>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-9">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((value) => (
                      <label key={value} className="flex min-h-11 items-center justify-center gap-1 text-sm text-navy-200">
                        <input type="radio" name="sleepinessLevel" value={value} checked={sleepinessLevel === value} onChange={() => setSleepinessLevel(value)} className="text-gold-400 focus:ring-gold-500" />
                        {value}
                      </label>
                    ))}
                  </div>
                  <div className="mt-1 flex justify-between text-xs text-navy-300"><span>Not sleepy</span><span>Very sleepy</span></div>
                </fieldset>
              </div>
            </details>

              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={!user || isLoading || !employeeId || !department}
                  className="w-full bg-gold-500 hover:bg-gold-600 sm:w-auto"
                >
                  {isLoading ? 'Starting Test...' : 'Start Test'}
                </Button>
              </div>
            </form>
        </div>
      </div>
    </div>
  );
};

export default TestSetup;
